import React, { useRef, useEffect, useCallback, useState } from "react";
import Header from "../components/Header";
import RequiredStar from "../components/RequiredStar"; // IMPORTED
import {
  Trash2,
  Plus,
  Upload,
  ArrowLeft,
  ArrowRight,
  Link as LinkIcon,
  Bold,
  Underline,
  Highlighter,
  FileText,
  Calendar,
  User,
  Users,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

/* --- CSS STYLES --- */
const pageStyles = `
  :root {
    --primary: #2563eb;
    --primary-hover: #1d4ed8;
    --muted: #6b7280;
    --bg: #f9fafb;
    --card-bg: #ffffff;
    --border: #e5e7eb;
    --text: #111827;
    --text-muted: #6b7280;
    --danger: #ef4444;
    --success: #10b981;
  }

  * { box-sizing: border-box; }
  body { margin: 0; font-family: 'Inter', sans-serif; background: var(--bg); }

  .main-content { 
    max-width: 1000px; 
    margin: 0 auto; 
    padding: 2rem 1rem 4rem; 
  }

  .page-container { animation: fadeIn 0.25s ease-out; }
  @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }

  .top-header-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1.5rem;
  }

  .section-title { 
    margin: 0;
    color: var(--text); 
    font-size: 1.5rem; 
    font-weight: 700;
    display: flex; align-items: center; gap: 0.75rem;
  }
  
  .section-subtitle {
    color: var(--text-muted);
    margin-top: 0.25rem;
    font-size: 0.95rem;
  }

  .btn-back-top {
    padding: 0.5rem 1rem;
    background: white;
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--text);
    font-weight: 600;
    font-size: 0.9rem;
    cursor: pointer;
    display: flex; align-items: center; gap: 0.5rem;
    transition: all 0.2s;
  }
  .btn-back-top:hover {
    border-color: var(--primary);
    color: var(--primary);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  }

  .form-card { 
    background: var(--card-bg); 
    padding: 1.5rem; 
    border-radius: 12px; 
    box-shadow: 0 1px 3px rgba(0,0,0,0.1); 
    border: 1px solid var(--border);
    margin-bottom: 1.5rem;
  }

  .card-header { 
    display: flex; 
    justify-content: space-between; 
    align-items: center; 
    margin-bottom: 1.5rem; 
    padding-bottom: 1rem; 
    border-bottom: 1px solid var(--border);
  }
  .card-title { font-size: 1.1rem; font-weight: 600; color: var(--text); margin: 0; }
  .header-actions { display: flex; align-items: center; gap: 0.75rem; }

  .form-group { margin-bottom: 1.25rem; }
  
  label { 
    display: flex; align-items: center; gap: 0.5rem;
    margin-bottom: 0.5rem; 
    font-weight: 500; 
    font-size: 0.9rem; 
    color: var(--text);
  }
  .icon { width: 16px; height: 16px; color: var(--primary); flex-shrink: 0; }

  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
  .grid-3 { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1.25rem; }

  input { 
    width: 100%; padding: 0.75rem 1rem; 
    border: 1px solid var(--border); 
    border-radius: 8px; 
    font-size: 0.95rem; 
    transition: all 0.2s;
    color: var(--text);
  }
  input:focus { 
    outline: none; 
    border-color: var(--primary); 
    box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
  }

  /* ERROR STYLE */
  .input-error { border-color: var(--danger) !important; background-color: #fef2f2; }
  .input-error:focus { box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15) !important; }

  .input-icon-wrapper { position: relative; width: 100%; }
  .input-icon { 
    position: absolute; left: 12px; top: 50%; transform: translateY(-50%); 
    color: var(--muted); pointer-events: none; display: flex;
  }
  .input-icon-wrapper input { padding-left: 2.5rem; }

  .btn-primary, .btn-secondary { 
    padding: 0.75rem 1.25rem; border-radius: 8px; font-weight: 600; 
    cursor: pointer; border: none; display: inline-flex; align-items: center; gap: 0.5rem; 
    transition: all 0.2s; font-size: 0.9rem;
  }
  .btn-primary { background: var(--primary); color: white; }
  .btn-primary:hover:not(:disabled) { background: var(--primary-hover); }
  .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
  .btn-secondary { background: transparent; color: var(--primary-hover); border: 1px solid var(--primary-hover); }
  .btn-secondary:hover { border-color: var(--primary); color: var(--primary); }

  .btn-dashed { 
    width: 100%; padding: 1rem; border: 2px dashed var(--primary); 
    background: transparent; color: var(--primary); font-weight: 600; 
    cursor: pointer; border-radius: 8px; display: flex; align-items: center; justify-content: center; 
    gap: 0.5rem; transition: background 0.2s;
  }
  .btn-dashed:hover { background: rgba(37,99,235,0.04); }

  .btn-danger-text { 
    background: none; border: none; color: var(--danger); cursor: pointer; 
    display: flex; align-items: center; gap: 0.25rem; font-size: 0.85rem; font-weight: 600;
    padding: 0.25rem 0.5rem; border-radius: 4px;
  }
  .btn-danger-text:hover { background: rgba(239,68,68,0.1); }

  .btn-toggle {
    background: rgba(37,99,235,0.05); border: 1px solid rgba(37,99,235,0.2);
    padding: 0.35rem 0.75rem; border-radius: 6px; color: var(--primary);
    display: inline-flex; align-items: center; gap: 0.4rem; cursor: pointer; font-size: 0.85rem; font-weight: 500;
  }
  .btn-toggle:hover { background: rgba(37,99,235,0.1); }

  .file-input-label { 
    position: relative; display: block; width: 100%; cursor: pointer;
  }
  .file-input-label input[type=file] { 
    position: absolute; top: 0; left: 0; width: 100%; height: 100%; opacity: 0; cursor: pointer;
  }
  .file-custom-btn { 
    width: 100%; padding: 0.875rem 1rem; border: 1px solid var(--border); 
    background: white; color: var(--text-muted); border-radius: 8px; 
    display: flex; align-items: center; justify-content: center; gap: 0.5rem;
    transition: all 0.2s; font-weight: 500;
  }
  .file-input-label:hover .file-custom-btn { 
    border-color: var(--primary); background: rgba(37,99,235,0.02); color: var(--primary);
  }

  .image-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 10px;
    margin-top: 12px;
  }
  .image-preview-item { 
    position: relative; 
    border-radius: 8px; 
    overflow: hidden; 
    border: 1px solid var(--border); 
    height: 100px;
    background: #f8fafc;
    display: flex; 
    align-items: center; 
    justify-content: center;
  }
  .image-preview-item img { 
    width: 100%; 
    height: 100%; 
    object-fit: cover; 
  }
  .btn-remove-img {
    position: absolute; 
    top: 4px; 
    right: 4px; 
    background: rgba(255,255,255,0.9); 
    border: 1px solid var(--danger);
    border-radius: 50%; 
    width: 24px; 
    height: 24px; 
    display: flex; 
    align-items: center; 
    justify-content: center;
    cursor: pointer; 
    color: var(--danger); 
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    transition: all 0.2s;
  }
  .btn-remove-img:hover {
    background: var(--danger);
    color: white;
  }

  .feature-grid { 
    display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 0.75rem; margin-top: 0.75rem;
  }
  .feature-item { 
    border: 1px solid var(--border); padding: 0.75rem; border-radius: 8px; 
    display: flex; align-items: flex-start; gap: 0.75rem; cursor: pointer; 
    transition: all 0.2s; font-size: 0.9rem; background: white;
  }
  .feature-item:hover { border-color: var(--primary); }
  .feature-item.checked { background: rgba(37,99,235,0.05); border-color: var(--primary); }
  .feature-checkbox { 
    width: 18px; height: 18px; border-radius: 4px; border: 2px solid var(--border); 
    flex-shrink: 0; position: relative; margin-top: 2px;
  }
  .feature-item.checked .feature-checkbox { background: var(--primary); border-color: var(--primary); }
  .feature-item.checked .feature-checkbox::after {
    content: '✓'; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); color: white; font-size: 10px; font-weight: 700;
  }

  .nav-buttons {
    display: flex; justify-content: space-between; align-items: center;
    margin-top: 2rem; padding-top: 1.5rem; border-top: 1px solid var(--border);
  }

  /* Rich Text Editor */
  .rich-editor-container { border: 1px solid var(--border); border-radius: 8px; overflow: hidden; background: white; }
  .rich-editor-container:focus-within { border-color: var(--primary); box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
  .rich-toolbar { display: flex; gap: 0.25rem; align-items: center; background: #f8fafc; padding: 0.5rem; border-bottom: 1px solid var(--border); flex-wrap: wrap; }
  .rich-btn { 
    background: white; border: 1px solid var(--border); padding: 0.375rem; border-radius: 6px; 
    cursor: pointer; color: var(--primary); width: 32px; height: 32px; 
    display: flex; align-items: center; justify-content: center; transition: all 0.2s;
  }
  .rich-btn:hover { background: var(--primary); color: white; border-color: var(--primary); }
  .toolbar-divider { width: 1px; height: 20px; background: #e5e7eb; margin: 0 6px; }
  
  .toggle-container {
    display: flex; align-items: center; cursor: pointer; padding: 4px 8px; border-radius: 6px; 
  }
  .toggle-container:hover { background: #e2e8f0; }
  .toggle-switch {
    position: relative; width: 32px; height: 18px; background-color: #94a3b8; border-radius: 20px; margin-right: 8px; flex-shrink: 0; transition: 0.3s;
  }
  .toggle-switch.on { background-color: var(--primary); }
  .toggle-thumb {
    position: absolute; top: 2px; left: 2px; width: 14px; height: 14px; background-color: white; border-radius: 50%; transition: 0.3s;
  }
  .toggle-switch.on .toggle-thumb { transform: translateX(14px); }
  .toggle-text { font-size: 0.8rem; color: #334155; font-weight: 600; }

  .rich-content { padding: 1rem; min-height: 100px; font-size: 0.95rem; line-height: 1.5; }
  .rich-content:empty:before { content: attr(data-placeholder); color: var(--text-muted); font-style: italic; }

  .patent-body { overflow: hidden; transition: max-height 0.3s ease, opacity 0.3s ease, margin 0.3s ease; opacity: 1; }
  .patent-body.collapsed { max-height: 0; opacity: 0; margin: 0; }
  .patent-body.expanded { max-height: 3000px; margin-top: 1rem; }

  @media (max-width: 768px) { 
    .grid-2, .grid-3 { grid-template-columns: 1fr; }
    .card-header { flex-direction: column; align-items: flex-start; gap: 1rem; }
    .header-actions { width: 100%; justify-content: space-between; }
    .top-header-row { flex-direction: column; gap: 1rem; align-items: flex-start; }
    .btn-back-top { width: 100%; justify-content: center; }
  }
`;

const RichTextEditor = ({
  value,
  onChange,
  placeholder = "Enter text...",
  className,
}) => {
  const editorRef = useRef(null);
  const [retainFormat, setRetainFormat] = useState(false);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      if (value === "" || document.activeElement !== editorRef.current) {
        editorRef.current.innerHTML = value || "";
      }
    }
  }, [value]);

  const execCmd = useCallback(
    (command, val = null) => {
      document.execCommand(command, false, val);
      if (editorRef.current) {
        editorRef.current.focus();
        onChange(editorRef.current.innerHTML);
      }
    },
    [onChange]
  );

  const toggleHighlight = useCallback(() => {
    try {
      const sel = window.getSelection && window.getSelection();
      if (!sel || sel.rangeCount === 0) {
        return;
      }
      // Try to get current highlight/backColor
      let curr = "";
      try {
        curr =
          document.queryCommandValue("hiliteColor") ||
          document.queryCommandValue("backColor") ||
          "";
      } catch (e) {
        curr = "";
      }
      const yellowHex = "#ffff00";
      const currNorm = (curr || "").replace(/\s+/g, "").toLowerCase();
      const isYellow =
        currNorm === yellowHex ||
        currNorm.includes("rgb(255,255,0)") ||
        currNorm.includes("255,255,0");
      if (isYellow) {
        // attempt to remove highlight
        document.execCommand("hiliteColor", false, "transparent");
        document.execCommand("backColor", false, "transparent");
      } else {
        document.execCommand("hiliteColor", false, yellowHex);
      }
      if (editorRef.current) {
        onChange(editorRef.current.innerHTML);
        editorRef.current.focus();
      }
    } catch (e) {
      // fallback: apply highlight
      document.execCommand("hiliteColor", false, "#ffff00");
      if (editorRef.current) onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const handlePaste = (e) => {
    if (!retainFormat) {
      e.preventDefault();
      const text = e.clipboardData.getData("text/plain");
      document.execCommand("insertText", false, text);
    }
  };

  return (
    <div className={`rich-editor-container ${className || ""}`}>
      <div className="rich-toolbar">
        <button
          type="button"
          className="rich-btn"
          onMouseDown={(e) => {
            e.preventDefault();
            execCmd("bold");
          }}
          title="Bold"
        >
          <Bold size={14} />
        </button>
        <button
          type="button"
          className="rich-btn"
          onMouseDown={(e) => {
            e.preventDefault();
            execCmd("underline");
          }}
          title="Underline"
        >
          <Underline size={14} />
        </button>
        <button
          type="button"
          className="rich-btn"
          onMouseDown={(e) => {
            e.preventDefault();
            toggleHighlight();
          }}
          title="Highlight"
        >
          <Highlighter size={14} style={{ color: "#e3bd00ff" }} />
        </button>

        <div className="toolbar-divider"></div>

        <div
          className="toggle-container"
          onClick={() => setRetainFormat(!retainFormat)}
        >
          <div className={`toggle-switch ${retainFormat ? "on" : ""}`}>
            <div className="toggle-thumb"></div>
          </div>
          <span className="toggle-text">
            {retainFormat ? "Plain Text" : "Keep Formatting"}
          </span>
        </div>
      </div>
      <div
        className="rich-content"
        contentEditable
        ref={editorRef}
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        onPaste={handlePaste}
        suppressContentEditableWarning={true}
        data-placeholder={placeholder}
      />
    </div>
  );
};

const stripHtml = (html) => {
  if (!html) return "";
  try {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  } catch (e) {
    return html.replace(/<[^>]*>/g, "").trim();
  }
};

const PriorArtPage = ({ data, updateData, next, back, onLogout }) => {
  const [collapsedMap, setCollapsedMap] = useState({});
  const [showErrors, setShowErrors] = useState(false); // NEW STATE for validation

  const visibleFeatures = (data.features || []).filter(
    (f) => f && f.text && f.text.trim() !== ""
  );

  // Local helper to determine if patent is filled (ID, Title, Link, Abstract, Description required)
  const isPatentFilled = (p) => {
    if (!p) return false;
    const hasId = p.patentId && p.patentId.trim() !== "";
    const hasTitle = p.title && p.title.trim() !== "";
    const hasLink = p.link && p.link.trim() !== "";
    const hasAbstract = stripHtml(p.abstract || "").trim() !== "";
    const hasDescription = stripHtml(p.description || "").trim() !== "";
    return hasId && hasTitle && hasLink && hasAbstract && hasDescription;
  };

  // VALIDATION LOGIC FOR NEXT BUTTON
  const handleNext = () => {
    const hasPatents = data.patents && data.patents.length > 0;

    // Check if ALL existing patents have both ID and Title
    const allValid = hasPatents && data.patents.every(isPatentFilled);

    if (!hasPatents || !allValid) {
      setShowErrors(true); // Trigger red highlight

      // Auto-expand any patent that has errors so user can see them
      const newCollapsedMap = { ...collapsedMap };
      data.patents.forEach((p) => {
        if (!isPatentFilled(p)) {
          newCollapsedMap[p.id] = false; // Expand invalid items
        }
      });
      setCollapsedMap(newCollapsedMap);
    } else {
      next(); // Proceed if valid
    }
  };

  const addPatent = useCallback(() => {
    const newPatent = {
      id: Date.now(),
      patentId: "",
      title: "",
      link: "",
      filedDate: "",
      inventor: "",
      assignee: "",
      abstract: "",
      description: "",
      images: [],
      image: null,
      selectedFeatures: [],
    };
    const newMap = {};
    (data.patents || []).forEach((p) => {
      newMap[p.id] = true;
    });
    newMap[newPatent.id] = false;
    setCollapsedMap(newMap);
    updateData("patents", [...(data.patents || []), newPatent]);
  }, [data.patents, updateData]);

  const hasPatents = (data.patents || []).length > 0;
  const allValidPatents =
    hasPatents && (data.patents || []).every(isPatentFilled);
  const priorArtValid = hasPatents && allValidPatents;

  const updatePatent = useCallback(
    (id, field, value) => {
      const updated = data.patents.map((p) =>
        p.id === id ? { ...p, [field]: value } : p
      );
      updateData("patents", updated);
    },
    [data.patents, updateData]
  );

  const handleImageUpload = useCallback(
    (e, id) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;

      const patent = data.patents.find((p) => p.id === id);
      const currentImages =
        patent.images || (patent.image ? [patent.image] : []);

      const readers = files.map((file) => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (evt) => resolve(evt.target.result);
          reader.readAsDataURL(file);
        });
      });

      Promise.all(readers).then((newImages) => {
        updatePatent(id, "images", [...currentImages, ...newImages]);
      });
      e.target.value = null;
    },
    [data.patents, updatePatent]
  );

  const removeImage = useCallback(
    (id, index) => {
      const patent = data.patents.find((p) => p.id === id);
      const currentImages =
        patent.images || (patent.image ? [patent.image] : []);
      const newImages = currentImages.filter((_, i) => i !== index);
      updatePatent(id, "images", newImages);
    },
    [data.patents, updatePatent]
  );

  const toggleFeature = useCallback(
    (patentId, featureId) => {
      const patent = data.patents.find((p) => p.id === patentId);
      const newFeatures = patent.selectedFeatures.includes(featureId)
        ? patent.selectedFeatures.filter((fid) => fid !== featureId)
        : [...patent.selectedFeatures, featureId];
      updatePatent(patentId, "selectedFeatures", newFeatures);
    },
    [data.patents, updatePatent]
  );

  const removePatent = useCallback(
    (id) => {
      updateData(
        "patents",
        data.patents.filter((p) => p.id !== id)
      );
    },
    [data.patents, updateData]
  );

  const toggleCollapse = useCallback((id) => {
    setCollapsedMap((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  return (
    <>
      <style>{pageStyles}</style>
      <Header active="priorArt" onLogout={onLogout} />
      <div className="main-content">
        <div className="page-container">
          <div className="top-header-row">
            <div>
              <h2 className="section-title">
                <FileText className="icon" size={24} />
                Prior Art References
              </h2>
              <div className="section-subtitle">
                Manage and document relevant patents
              </div>
            </div>

            <button className="btn-back-top" onClick={back}>
              <ArrowLeft size={16} /> Back
            </button>
          </div>

          {(data.patents || []).length === 0 ? (
            <div
              className="form-card empty-state"
              style={{ textAlign: "center", padding: "3rem 1rem" }}
            >
              <FileText
                style={{
                  width: 48,
                  height: 48,
                  color: "var(--muted)",
                  opacity: 0.5,
                  marginBottom: "1rem",
                }}
              />
              <h3 style={{ color: "var(--text)", margin: "0 0 0.5rem 0" }}>
                No references added
              </h3>
              <p style={{ margin: "0 0 1.5rem 0", color: "var(--text-muted)" }}>
                Add patent references to complete your prior art analysis
              </p>
              <button className="btn-dashed" onClick={addPatent}>
                <Plus size={16} /> Add First Reference
              </button>

              {/* Show error message if tried to proceed with 0 patents */}
              {showErrors && (data.patents || []).length === 0 && (
                <div
                  style={{
                    color: "#ef4444",
                    marginTop: "1rem",
                    fontWeight: "600",
                  }}
                >
                  Please add at least one patent reference.
                </div>
              )}
            </div>
          ) : (
            <>
              {data.patents.map((p, index) => {
                const isCollapsed = !!collapsedMap[p.id];
                return (
                  <div key={p.id} className="form-card">
                    <div className="card-header">
                      <h3 className="card-title">Reference #{index + 1}</h3>
                      <div className="header-actions">
                        <button
                          type="button"
                          className="btn-toggle"
                          onClick={() => toggleCollapse(p.id)}
                        >
                          {isCollapsed ? (
                            <ChevronDown size={14} />
                          ) : (
                            <ChevronUp size={14} />
                          )}
                          {isCollapsed ? "Expand" : "Collapse"}
                        </button>
                        <button
                          className="btn-danger-text"
                          onClick={() => removePatent(p.id)}
                        >
                          <Trash2 size={14} /> Remove
                        </button>
                      </div>
                    </div>

                    <div
                      className={`patent-body ${
                        isCollapsed ? "collapsed" : "expanded"
                      }`}
                    >
                      <div className="grid-2">
                        <div className="form-group">
                          <label>
                            <FileText className="icon" /> Patent ID{" "}
                            <RequiredStar />
                          </label>
                          <input
                            value={p.patentId}
                            onChange={(e) =>
                              updatePatent(p.id, "patentId", e.target.value)
                            }
                            placeholder="US9770272B2"
                            // RED BORDER IF ERROR
                            className={
                              showErrors && (!p.patentId || !p.patentId.trim())
                                ? "input-error"
                                : ""
                            }
                          />
                          {showErrors &&
                            (!p.patentId || !p.patentId.trim()) && (
                              <div
                                style={{
                                  color: "#ef4444",
                                  fontSize: "0.8rem",
                                  marginTop: "4px",
                                }}
                              >
                                Required
                              </div>
                            )}
                        </div>
                        <div className="form-group">
                          <label>
                            Patent Title <RequiredStar />
                          </label>
                          <input
                            value={p.title}
                            onChange={(e) =>
                              updatePatent(p.id, "title", e.target.value)
                            }
                            placeholder="Enter title"
                            // RED BORDER IF ERROR
                            className={
                              showErrors && (!p.title || !p.title.trim())
                                ? "input-error"
                                : ""
                            }
                          />
                          {showErrors && (!p.title || !p.title.trim()) && (
                            <div
                              style={{
                                color: "#ef4444",
                                fontSize: "0.8rem",
                                marginTop: "4px",
                              }}
                            >
                              Required
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="grid-3">
                        <div className="form-group">
                          <label>
                            <Calendar className="icon" /> Filed Date
                          </label>
                          <input
                            type="date"
                            value={p.filedDate || ""}
                            onChange={(e) =>
                              updatePatent(p.id, "filedDate", e.target.value)
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label>
                            <User className="icon" /> Inventor
                          </label>
                          <input
                            value={p.inventor || ""}
                            onChange={(e) =>
                              updatePatent(p.id, "inventor", e.target.value)
                            }
                            placeholder=" Inventors' name"
                          />
                        </div>
                        <div className="form-group">
                          <label>
                            <Users className="icon" /> Assignee
                          </label>
                          <input
                            value={p.assignee || ""}
                            onChange={(e) =>
                              updatePatent(p.id, "assignee", e.target.value)
                            }
                            placeholder="Company Inc."
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>
                          <LinkIcon className="icon" /> Reference Link{" "}
                          <RequiredStar />
                        </label>
                        <div className="input-icon-wrapper">
                          <LinkIcon size={16} className="input-icon" />
                          <input
                            value={p.link}
                            onChange={(e) =>
                              updatePatent(p.id, "link", e.target.value)
                            }
                            placeholder="https://patents.google.com/patent/..."
                            className={
                              showErrors && (!p.link || !p.link.trim())
                                ? "input-error"
                                : ""
                            }
                          />
                        </div>
                        {showErrors && (!p.link || !p.link.trim()) && (
                          <div
                            style={{
                              color: "#ef4444",
                              fontSize: "0.8rem",
                              marginTop: "4px",
                            }}
                          >
                            Required
                          </div>
                        )}
                      </div>

                      <div className="form-group">
                        <label>
                          Abstract <RequiredStar />
                        </label>
                        <RichTextEditor
                          value={p.abstract}
                          onChange={(val) =>
                            updatePatent(p.id, "abstract", val)
                          }
                          placeholder="Summarize the abstract..."
                          className={
                            showErrors &&
                            stripHtml(p.abstract || "").trim() === ""
                              ? "input-error"
                              : ""
                          }
                        />
                        {showErrors &&
                          stripHtml(p.abstract || "").trim() === "" && (
                            <div
                              style={{
                                color: "#ef4444",
                                fontSize: "0.8rem",
                                marginTop: "4px",
                              }}
                            >
                              Required
                            </div>
                          )}
                      </div>

                      <div className="form-group">
                        <label>
                          Detailed Description <RequiredStar />
                        </label>
                        <RichTextEditor
                          value={p.description}
                          onChange={(val) =>
                            updatePatent(p.id, "description", val)
                          }
                          placeholder="Key technical description..."
                          className={
                            showErrors &&
                            stripHtml(p.description || "").trim() === ""
                              ? "input-error"
                              : ""
                          }
                        />
                        {showErrors &&
                          stripHtml(p.description || "").trim() === "" && (
                            <div
                              style={{
                                color: "#ef4444",
                                fontSize: "0.8rem",
                                marginTop: "4px",
                              }}
                            >
                              Required
                            </div>
                          )}
                      </div>

                      {/* MULTIPLE IMAGE UPLOAD SECTION */}
                      <div className="form-group">
                        <label>Patent Figures (Upload Multiple)</label>
                        <label className="file-input-label">
                          <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, p.id)}
                          />
                          <div className="file-custom-btn">
                            <Upload size={16} />
                            {p.images && p.images.length > 0
                              ? "Add More Images"
                              : "Upload Images"}
                          </div>
                        </label>

                        {/* Render Image Grid */}
                        <div className="image-grid">
                          {(p.images && p.images.length > 0
                            ? p.images
                            : p.image
                            ? [p.image]
                            : []
                          ).map((img, idx) => (
                            <div key={idx} className="image-preview-item">
                              <img src={img} alt={`Patent Figure ${idx + 1}`} />
                              <button
                                className="btn-remove-img"
                                onClick={() => removeImage(p.id, idx)}
                                title="Remove Image"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Relevant Features:</label>
                        {visibleFeatures.length === 0 ? (
                          <div
                            style={{
                              padding: "1rem",
                              textAlign: "center",
                              color: "var(--text-muted)",
                              fontSize: "0.9rem",
                              border: "1px dashed var(--border)",
                              borderRadius: "8px",
                            }}
                          >
                            No features defined yet
                          </div>
                        ) : (
                          <div className="feature-grid">
                            {visibleFeatures.map((f) => (
                              <div
                                key={f.id}
                                className={`feature-item ${
                                  p.selectedFeatures.includes(f.id)
                                    ? "checked"
                                    : ""
                                }`}
                                onClick={() => toggleFeature(p.id, f.id)}
                              >
                                <div className="feature-checkbox"></div>
                                <div>{f.text}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              <button className="btn-dashed" onClick={addPatent}>
                <Plus size={16} /> Add New Reference
              </button>
            </>
          )}

          <div className="nav-buttons">
            <button className="btn-secondary" onClick={back}>
              <ArrowLeft size={16} /> Back
            </button>
            <button className="btn-primary" onClick={handleNext}>
              Review & Generate <ArrowRight size={16} />
            </button>
          </div>
          {showErrors && !priorArtValid && (
            <div style={{ color: "#ef4444", fontWeight: 700, marginTop: 10 }}>
              Please fill all required fields on the references before
              proceeding.
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PriorArtPage;
